import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import WaveLoader from './WaveLoader';
import { ClipButton } from './ReportWorkspace';
import '../styles/SignalIntelligencePanel.css';
import filterParams from '../utils/filterParams';

const label = (value) => (value ? String(value).replaceAll('_', ' ') : 'Not assessed');

function RelatedList({ title, items, empty }) {
  return (
    <section className="signal-section">
      <h4>{title}</h4>
      {items?.length ? (
        <div className="signal-chips">
          {items.map((item) => (
            <span className="signal-chip" key={`${item.entity_type}-${item.entity_name}`}>
              {item.entity_name}
              <small>{item.conversation_count} conversations</small>
            </span>
          ))}
        </div>
      ) : (
        <p className="signal-muted">{empty}</p>
      )}
    </section>
  );
}

export default function SignalIntelligencePanel({
  entityName,
  filters,
  onClose,
  onOpenConversation,
  embedded = false,
  fullWidth = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEvidence, setShowEvidence] = useState(false);

  useEffect(() => {
    if (!entityName) return;
    const controller = new AbortController();
    const params = filterParams(filters, { entity_name: entityName });
    setLoading(true);
    setError('');
    axios
      .get(`/api/intelligence/signal?${params}`, { signal: controller.signal })
      .then((response) => setData(response.data))
      .catch((err) => {
        if (!axios.isCancel(err)) setError(err.response?.data?.error || 'Unable to load this signal.');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [entityName, filters]);

  const trendData = useMemo(
    () =>
      data?.trend?.length
        ? [
            {
              x: data.trend.map((row) => row.period),
              y: data.trend.map((row) => row.conversation_count),
              text: data.trend.map((row) => `${row.contributor_count} field contributor(s)`),
              type: 'scatter',
              mode: 'lines+markers',
              line: { color: '#1f5eff', width: 3 },
              marker: { size: 7 },
              hovertemplate: '%{x}<br>%{y} conversations<br>%{text}<extra></extra>',
            },
          ]
        : [],
    [data],
  );

  if (!entityName && embedded)
    return (
      <aside className="signal-panel signal-panel-embedded signal-panel-empty">
        <div>
          <h3>Conversation evidence</h3>
          <p>Select a row or coloured cell to see the comments, locations and related topics behind it.</p>
        </div>
      </aside>
    );
  if (!entityName) return null;
  const signal = data?.signal || {};
  const change = data?.movement?.change_percent;
  // Conversations with a scored sentiment — the count the heatmaps and maps use. Usually
  // fewer than the conversations that merely mention the entity.
  const scored = (data?.sentiment || [])
    .filter((row) => row.label !== 'Not assessed')
    .reduce((n, row) => n + Number(row.conversation_count || 0), 0);

  if (embedded && !fullWidth) {
    const comments = data?.evidence?.slice(0, 2) || [];
    return (
      <>
        <aside className="signal-panel signal-panel-embedded signal-context-card">
          <header className="signal-panel-header">
            <div>
              <span>Conversation context</span>
              <h2>{entityName}</h2>
              <p>Representative comments explaining the selected view.</p>
            </div>
          </header>
          {loading && (
            <div className="signal-loading">
              <WaveLoader size="small" />
            </div>
          )}
          {error && <div className="signal-error">{error}</div>}
          {!loading && data && (
            <div className="signal-panel-body signal-context-body">
              <p className="signal-context-observation">{data.observation}</p>
              <div className="signal-evidence-list signal-context-comments">
                {comments.map((item, index) => (
                  <article key={`${item.conversation_id}-${index}`}>
                    <blockquote>“{item.comment}”</blockquote>
                    <div>
                      {item.call_date || 'Date unavailable'} · {item.region_name} ·{' '}
                      {item.agent_name || 'Field contributor'}
                    </div>
                  </article>
                ))}
                {!comments.length && (
                  <p className="signal-muted">No supporting comments are available in this selection.</p>
                )}
              </div>
              <button className="signal-view-evidence" onClick={() => setShowEvidence(true)}>
                View all supporting conversations
              </button>
            </div>
          )}
        </aside>
        {showEvidence && (
          <SignalIntelligencePanel
            entityName={entityName}
            filters={filters}
            onClose={() => setShowEvidence(false)}
            onOpenConversation={onOpenConversation}
          />
        )}
      </>
    );
  }

  return (
    <div
      className={embedded ? 'signal-embedded-wrap' : 'signal-overlay'}
      onClick={embedded ? undefined : onClose}
    >
      <aside
        className={`signal-panel ${embedded ? 'signal-panel-embedded' : ''}`}
        onClick={(event) => event.stopPropagation()}
        aria-label={`${entityName} evidence`}
      >
        <header className="signal-panel-header">
          <div>
            <span>Conversation evidence</span>
            <h2>{entityName}</h2>
            <p>{signal.entity_type || 'Topic found in field conversations'}</p>
          </div>
          {data && (
            <ClipButton
              item={{
                page: 'Conversation evidence',
                title: `${entityName}: conversation evidence`,
                subtitle: 'Recorded comments and source conversation IDs',
                visual: 'evidence',
                scope:
                  [
                    filters?.date_from && `From ${filters.date_from}`,
                    filters?.date_to && `to ${filters.date_to}`,
                    filters?.province && `Province ${filters.province}`,
                  ]
                    .filter(Boolean)
                    .join(' ') || 'Current dashboard scope',
                finding: data.observation || 'Review the cited conversations.',
                next_check: data.action_prompt,
                rows: (data.evidence || []).slice(0, 3).map((x) => ({
                  label: `${x.call_date || 'Undated'} · conversation ${x.conversation_id}`,
                  value: x.comment,
                  status: 'none',
                })),
              }}
            />
          )}
          {!embedded && (
            <button onClick={onClose} aria-label="Close">
              ×
            </button>
          )}
        </header>

        {loading && (
          <div className="signal-loading">
            <WaveLoader size="small" />
          </div>
        )}
        {error && <div className="signal-error">{error}</div>}
        {!loading && data && (
          <div className="signal-panel-body">
            <div className="signal-facts">
              <div>
                <strong>{signal.conversation_count || 0}</strong>
                <span>Conversations mentioning it</span>
              </div>
              <div>
                <strong>{scored}</strong>
                <span>Scored for sentiment</span>
              </div>
              <div>
                <strong>{signal.contributor_count || 0}</strong>
                <span>Field contributors</span>
              </div>
              <div>
                <strong>{signal.region_count || 0}</strong>
                <span>Locations</span>
              </div>
              <div>
                <strong>
                  {change == null ? label(data.movement?.direction) : `${change > 0 ? '+' : ''}${change}%`}
                </strong>
                <span>Recent movement</span>
              </div>
            </div>

            <section className="signal-section signal-observation">
              <h4>What is happening</h4>
              <p>{data.observation}</p>
              <small>Observed from FieldForce conversations in the selected period.</small>
            </section>

            <section className="signal-section">
              <h4>How it is changing</h4>
              {trendData.length ? (
                <Plot
                  data={trendData}
                  layout={{
                    height: 230,
                    margin: { t: 10, r: 15, b: 45, l: 45 },
                    xaxis: { title: 'Week', fixedrange: true },
                    yaxis: { title: 'Conversations', fixedrange: true, rangemode: 'tozero' },
                    paper_bgcolor: '#fffdf8',
                    plot_bgcolor: '#fffdf8',
                    showlegend: false,
                  }}
                  config={{ responsive: true, displayModeBar: false }}
                  style={{ width: '100%' }}
                />
              ) : (
                <p className="signal-muted">Not enough dated evidence to show a timeline.</p>
              )}
            </section>

            <RelatedList
              title="Problems discussed with it"
              items={data.related?.problems}
              empty="No approved problem entities are linked yet."
            />
            <RelatedList
              title="Products discussed with it"
              items={data.related?.products}
              empty="No related products are visible in this selection."
            />
            <RelatedList
              title="Companies discussed with it"
              items={data.related?.companies}
              empty="No company comparisons are visible in this selection."
            />

            <section className="signal-section">
              <h4>Where it is being reported</h4>
              <div className="signal-region-list">
                {data.regions?.map((region) => (
                  <div key={region.region_name}>
                    <span>{region.region_name}</span>
                    <strong>{region.conversation_count}</strong>
                  </div>
                ))}
              </div>
            </section>

            <section className="signal-section signal-action">
              <h4>What to check next</h4>
              <p>{data.action_prompt}</p>
              <small>This is a prompt for review, not a claimed recommendation or campaign outcome.</small>
            </section>

            <section className="signal-section">
              <h4>Supporting conversation comments</h4>
              <div className="signal-evidence-list">
                {data.evidence?.map((item, index) => (
                  <article key={`${item.conversation_id}-${index}`}>
                    <blockquote>“{item.comment}”</blockquote>
                    <div>
                      {item.call_date || 'Date unavailable'} · {item.region_name} ·{' '}
                      {item.agent_name || 'Field contributor'}
                    </div>
                    <div className="signal-tags">
                      {item.signal_type && <span>{label(item.signal_type)}</span>}
                      {item.sentiment_polarity && <span>{label(item.sentiment_polarity)}</span>}
                      {item.confidence != null && (
                        <span>{Math.round(Number(item.confidence) * 100)}% extraction confidence</span>
                      )}
                    </div>
                    {onOpenConversation && (
                      <button onClick={() => onOpenConversation(item.conversation_id)}>
                        Open conversation {item.conversation_id}
                      </button>
                    )}
                  </article>
                ))}
              </div>
            </section>

            <div className="signal-limit-note">
              <strong>Current boundary:</strong> {data.limits?.note}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
